import { Injectable } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { DeviceAuthenticationStatusService } from '@shure/shared/angular/data-access/device-authentication';
import { LiveDeviceDiscoveryApiService } from '@shure/shared/angular/data-access/device-discovery';
import {
	ApolloQueryError,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import { RequestResponse, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceReadonlyStateProvider } from '@shure/shared/angular/utils/device-readonly';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import { SpectrumScannerApiService, SpectrumScannerDevice, SpectrumScannerRoomDevice } from '../api';

import {
	ClearSpectrumScannerMutationGQL,
	GetSpectrumScannerDeviceQueryGQL,
	SpectrumScannerDeviceSubscriptionGQL,
	SpectrumScannerRoomDevicesSubscriptionGQL,
	StartSpectrumScannerMutationGQL,
	StopSpectrumScannerMutationGQL,
	GetSpectrumScannerRoomDevicesQueryGQL
} from './graphql/generated/spectrum-scanner.generated';
import { mapSpectrumScannerDeviceFromSysApi, mapSpectrumScannerRoomDevicesFromSysApi } from './mappers';

@Injectable()
export class SysApiSpectrumScannerApiService implements SpectrumScannerApiService {
	private readonly logger: ILogger;

	public readonly deviceSubscription: CombiningSubscriptionManager;
	public readonly roomDevicesSubscription: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		subscriptionManagerService: SubscriptionManagerService,
		deviceSubscriptionGQL: SpectrumScannerDeviceSubscriptionGQL,
		roomDevicesSubscriptionGQL: SpectrumScannerRoomDevicesSubscriptionGQL,
		private readonly getSpectrumScannerDeviceQuery: GetSpectrumScannerDeviceQueryGQL,
		private readonly startSpectrumScannerMutation: StartSpectrumScannerMutationGQL,
		private readonly stopSpectrumScannerMutation: StopSpectrumScannerMutationGQL,
		private readonly clearSpectrumScannerMutation: ClearSpectrumScannerMutationGQL,
		private readonly getSpectrumScannerRoomDevicesQueryGQL: GetSpectrumScannerRoomDevicesQueryGQL,
		private readonly deviceDiscoveryApiService: LiveDeviceDiscoveryApiService,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService,
		private readonly readonlyStateProvider: DeviceReadonlyStateProvider
	) {
		this.logger = logger.createScopedLogger('SysApiSpectrumScannerApiService');

		this.deviceSubscription = new CombiningSubscriptionManager(
			{
				name: 'SysApiSpectrumScannerSubscriptionsService:Device',
				changeTypes: [
					NodeChangeType.DeviceDectRfSpectrumScan,
					NodeChangeType.DeviceName,
					NodeChangeType.DeviceRfBand,
					NodeChangeType.DeviceAuthentication
				],
				subscriptionGql: deviceSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);

		this.roomDevicesSubscription = new CombiningSubscriptionManager(
			{
				name: 'SysApiSpectrumScannerSubscriptionsService:Room',
				changeTypes: [NodeChangeType.Room],
				subscriptionGql: roomDevicesSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getDevice$(deviceId: string): Observable<SpectrumScannerDevice> {
		const queryId = uuidv4();
		this.logger.trace('getDevice$', 'Getting spectrum scanner device', { queryId, deviceId });
		return combineLatest([
			this.getSpectrumScannerDeviceQuery.watch({ id: deviceId }, { errorPolicy: 'all' }).valueChanges,
			this.deviceAuthenticationStatusService.getDeviceState$(deviceId),
			this.readonlyStateProvider.isReadonly$(deviceId)
		]).pipe(
			map(([query, auth, isReadonly]) => {
				if (!query.data?.node || !('features' in query.data.node)) {
					throw ApolloQueryErrorMapper.getError(query);
				}

				const device = mapSpectrumScannerDeviceFromSysApi(query.data.node, auth.authenticated, isReadonly);
				this.logger.trace('getDevice$', 'Query updated', { queryId, deviceId });

				return device;
			}),
			tapOnce((device) => this.deviceSubscription.subscribe(queryId, device.id)),
			noMoreSubscribers(() => this.deviceSubscription.unsubscribeQuery(queryId)),
			catchError((error: Error) => {
				this.logger.error('getDevice$()', 'Failed to query device', {
					deviceId,
					error
				});
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public getDevices$(deviceIds: string[]): Observable<SpectrumScannerDevice[]> {
		const queryId = uuidv4();
		this.logger.trace('getDevices$', 'Getting spectrum scanner devices', { queryId, deviceIds });
		return combineLatest(deviceIds.map((deviceId) => this.getDevice$(deviceId)));
	}

	public getDevicesInRoom$(id: string): Observable<SpectrumScannerRoomDevice[]> {
		const queryId = uuidv4();
		this.logger.trace('getDevicesInRoom()', 'Querying devices in room', { id, queryId });
		return this.getSpectrumScannerRoomDevicesQueryGQL.watch({ id }, { errorPolicy: 'all' }).valueChanges.pipe(
			map((query) => {
				if (!query.data?.node || !('devices' in query.data.node)) {
					throw ApolloQueryErrorMapper.getError(query);
				}

				const devices = mapSpectrumScannerRoomDevicesFromSysApi(query.data.node);
				this.logger.trace('getDevicesInRoom', 'Query updated', { queryId, id });

				return devices;
			}),
			tapOnce(() => this.roomDevicesSubscription.subscribe(queryId, id)),
			noMoreSubscribers(() => this.roomDevicesSubscription.unsubscribeQuery(queryId)),
			catchError((error: Error) => {
				this.logger.error('getDevicesInRoom()', 'Failed to query devices in room', { roomId: id, error });
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public getAllScannableDeviceIds$(): Observable<string[]> {
		return this.deviceDiscoveryApiService
			.getDiscoveredDevicesByQuery$<SpectrumScannerDevice>((id) => this.getDevice$(id), { throttleMs: 10 })
			.pipe(
				map((devices) => devices.filter((d) => !!d.scanStatus && !d.scanStatus.isMissing).map((d) => d.id)),
				catchError((error: Error) => {
					this.logger.error('getAllScannableDeviceIds$()', 'Failed to query scannable device online', {
						error
					});
					return of([]);
				}),
				shareReplay({ refCount: true, bufferSize: 1 })
			);
	}

	public startScan(deviceIds: string[]): Observable<RequestResponse<string>[]> {
		return combineLatest(
			deviceIds.map((deviceId) =>
				this.startSpectrumScannerMutation
					.mutate({
						id: deviceId,
						injectKeyring: '//supplied-by-keyring-injector'
					})
					.pipe(
						map(() => {
							this.logger.trace('startScan()', 'Done', { deviceId });
							return { state: ResponseState.Done };
						}),
						catchError((error: Error) => {
							this.logger.error('startScan()', 'Error', { error });
							return of({ state: ResponseState.Error, error: error.message });
						})
					)
			)
		);
	}

	public endScan(deviceIds: string[]): Observable<RequestResponse<string>[]> {
		return combineLatest(
			deviceIds.map((deviceId) =>
				this.stopSpectrumScannerMutation
					.mutate({
						id: deviceId,
						injectKeyring: '//supplied-by-keyring-injector'
					})
					.pipe(
						map(() => {
							this.logger.trace('endScan()', 'Done', { deviceId });
							return { state: ResponseState.Done };
						}),
						catchError((error: Error) => {
							this.logger.error('endScan()', 'Error', { error });
							return of({ state: ResponseState.Error, error: error.message });
						})
					)
			)
		);
	}

	public clearScan(deviceIds: string[]): Observable<RequestResponse<string>[]> {
		return combineLatest(
			deviceIds.map((deviceId) =>
				this.clearSpectrumScannerMutation
					.mutate({
						id: deviceId,
						injectKeyring: '//supplied-by-keyring-injector'
					})
					.pipe(
						map(() => {
							this.logger.trace('clearScan()', 'Done', { deviceId });
							return { state: ResponseState.Done };
						}),
						catchError((error: Error) => {
							this.logger.error('clearScan()', 'Error', { error });
							return of({ state: ResponseState.Error, error: error.message });
						})
					)
			)
		);
	}
}
